/**
 * Um cartão do dashboard como nó da tela — indicador, gráfico ou tabela.
 *
 * **Um renderer para os três**, porque os três têm contrato idêntico
 * (`events`, `dashboard`, `name`, `item`, `showConfiguration`) e nenhum deles
 * referencia gridster no próprio template: o gridster é do container do
 * dashboard, não do cartão. É o que torna esta ponte estreita — o cartão sai do
 * dashboard e entra num painel da tela sem adaptação.
 *
 * Nada em `system-view/main-dashboard/` foi reescrito. São 6.010 linhas de
 * código recente, 100% NG-Zorro sobre tokens `--tem-*`, com re-tematização no
 * toggle claro/escuro e auto-refresh com gate de foco. Reescrever isso dentro do
 * construtor seria destruir valor.
 *
 * ---
 *
 * **O encaixe que faz a diferença: os campos da tela filtram o cartão.**
 *
 * O cartão não recebe dados prontos. Ele monta a própria consulta e a resolve
 * por `DashboardViewService.applyFilters(query, form)`, que varre a consulta
 * atrás de `{@NOME}` e busca `form.get('NOME')`. O casamento é POR NOME DE
 * CONTROLE, e o gatilho de recarga é o `events` — toda vez que aquele
 * observable emite um formulário, o cartão reconsulta.
 *
 * Então este renderer monta um `FormGroup` SINTÉTICO cujos nomes de controle são
 * os `paramName` da configuração, alimentado pelos campos que o usuário escolheu
 * no inspector, e empurra esse formulário pelo `events` a cada mudança.
 *
 * Resultado prático: escolher um contrato num select da tela redesenha o
 * gráfico. É o que separa "dashboard colado na tela" de "dashboard da tela".
 *
 * O mapeamento é explícito (`DashboardFilterBinding`) justamente para o campo da
 * tela não precisar se chamar igual ao parâmetro da consulta — esse tipo de
 * acoplamento por nome ninguém mantém.
 */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { MainDashboardModule } from '../../../system-view/main-dashboard/main-dashboard.module';
import { DashboardViewService } from '../../../services/dashboard.services';
import { UserAuthenticationServices } from '../../../services/user-authentication.services';
import type { DashBoardInterface } from '../../../system-view/main-dashboard/interfaces/dashboard';
import { DashboardFilterBinding } from '../../model/dashboard-schema';
import { ScreenNode } from '../../model/node-props';
import { resolvePath } from '../../runtime/data-binding';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { ScreenContext } from '../screen-context';

/**
 * Atraso entre a digitação e a nova consulta.
 *
 * O cartão reconsulta a cada emissão do `events`. Sem atraso, digitar num campo
 * ligado a um filtro dispararia uma consulta por tecla — e a consulta do cartão
 * é um SELECT no banco, não um cálculo local.
 */
export const CARD_FILTER_DEBOUNCE_MS = 350;

@Component({
  selector: 'tem-dashboard-card',
  standalone: true,
  imports: [CommonModule, MainDashboardModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Uma instância do serviço POR CARTÃO.
  //
  // O `main-dashboard` provê no componente dele pelo mesmo motivo, e o comentário
  // de lá explica: o serviço guarda o contexto de empresa e contrato, e duas
  // instâncias compartilhando corrompem o contexto uma da outra. Por cartão gasta
  // um objeto a mais e não tem esse risco.
  providers: [DashboardViewService],
  template: `
    <div class="tem-card" [style.min-height.px]="minHeight()">
      @switch (kind()) {

        @case ('minicard') {
          <mini-card
            [events]="filterBus"
            [dashboard]="hostDashboard()"
            [name]="cardName()"
            [item]="config()"
            [showConfiguration]="false" />
        }

        @case ('chart') {
          <chart-card
            [events]="filterBus"
            [dashboard]="hostDashboard()"
            [name]="cardName()"
            [item]="config()"
            [showConfiguration]="false" />
        }

        @case ('table') {
          <table-card
            [events]="filterBus"
            [dashboard]="hostDashboard()"
            [name]="cardName()"
            [item]="config()"
            [showConfiguration]="false" />
        }
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    /* A altura mínima existe para a tela não pular enquanto o cartão consulta.
       Sem ela, um painel com três indicadores muda de altura duas vezes ao
       abrir, e o que está embaixo dança. */
    .tem-card { display: block; }
  `]
})
export class DashboardCardRenderer implements NodeRenderer<'dashboardCard'>, OnInit {

  readonly node = input.required<ScreenNode<'dashboardCard'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly context = inject(ScreenContext, { optional: true });
  private readonly dvs = inject(DashboardViewService);
  private readonly uas = inject(UserAuthenticationServices);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * O barramento de filtro que o cartão escuta.
   *
   * `BehaviorSubject` e não `Subject`: o cartão se inscreve no `ngOnInit`, que
   * roda DEPOIS deste construtor. Com um `Subject`, a primeira emissão seria
   * perdida e o cartão consultaria sem os filtros da tela — e só se corrigiria
   * quando o usuário mexesse em algum campo.
   */
  readonly filterBus = new BehaviorSubject<FormGroup | null>(null);

  /** O formulário sintético, com um controle por parâmetro da consulta. */
  private readonly synthetic = new FormGroup<Record<string, FormControl>>({});

  /** Inscrição no formulário da tela. Trocada quando o formulário muda. */
  private formWatch: Subscription | null = null;

  readonly kind = computed(() => this.node().props.kind);

  readonly cardName = computed<string>(() => this.node().id);

  readonly minHeight = computed<number>(() => this.node().props.minHeight ?? 0);

  /** A configuração do cartão, no formato que o dashboard persiste. */
  readonly config = computed<never>(() => {
    const props = this.node().props;

    switch (props.kind) {
      case 'minicard':
        return (props.minicard ?? {}) as never;
      case 'chart':
        return (props.chart ?? {}) as never;
      default:
        return (props.table ?? {}) as never;
    }
  });

  /**
   * Um `DashBoardInterface` sintético.
   *
   * O cartão lê três coisas dele: `tabName` (rótulo interno), `iddashboard` (só
   * para gravar preferência do usuário) e `filter` (lista de filtros da aba, que
   * aqui não existe). Passar `null` faria os três estourarem em pontos
   * diferentes; passar um objeto mínimo e coerente é mais honesto que remendar
   * o cartão.
   */
  readonly hostDashboard = computed<DashBoardInterface>(() => ({
    iddashboard: 0,
    tabName: this.node().id,
    tabTitle: '',
    filter: [],
    gadGets: []
  }));

  constructor() {
    // O contexto de empresa, contrato e usuário. É o que o `applyFilters`
    // resolve sozinho para os `PARAM_*` de ambiente — sem isto, uma consulta
    // que use `{@PARAM_CODECOMPANY}` receberia NULL e devolveria nada.
    this.dvs.idUser = this.uas.user.iduser;
    this.dvs.codeCompanyList = this.uas.getCompanies();
    this.dvs.codeContractList = this.uas.getContracts();
    this.dvs.codeCompany = Number(this.uas.user.codecompany);

    // Reconstrói o formulário sintético quando a configuração ou os dados
    // mudam, e empurra pelo barramento. Um `effect` porque as duas entradas são
    // signals: mexer no mapeamento pelo inspector redesenha o cartão na hora.
    effect(() => this.publishFilters());

    // O valor de um controle NÃO é signal: mexer num select da tela não faz o
    // efeito acima rodar. Esta inscrição é o que fecha o circuito — e é ela que
    // cumpre a promessa da ponte, "escolher um contrato redesenha o gráfico".
    effect(onCleanup => {
      const screenForm = this.form();

      if (screenForm === null) {
        return;
      }

      const watch = screenForm.valueChanges
        .pipe(debounceTime(CARD_FILTER_DEBOUNCE_MS))
        .subscribe(() => this.publishFilters());

      onCleanup(() => watch.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.formWatch?.unsubscribe();
      this.filterBus.complete();
    });
  }

  /**
   * Monta o formulário sintético e o publica.
   *
   * Cada `paramName` vira um controle com esse nome exato — é o que o
   * `applyFilters` procura. O valor vem do campo da tela (pelo id do nó) ou de
   * um caminho no dado; um mapeamento sem origem definida entra como `null`, e
   * a consulta trata como filtro ausente em vez de quebrar.
   */
  private publishFilters(): void {
    const bindings = this.node().props.filters ?? [];
    const screenForm = this.form();
    const screenData = this.context?.data() ?? null;

    let changed = false;

    for (const binding of bindings) {
      const value = this.valueOf(binding, screenForm, screenData);
      const existing = this.synthetic.get(binding.paramName);

      if (existing === null) {
        this.synthetic.addControl(binding.paramName, new FormControl(value));
        changed = true;
        continue;
      }

      if (existing.value !== value) {
        existing.setValue(value, { emitEvent: false });
        changed = true;
      }
    }

    // Só publica quando algum filtro MUDOU de fato.
    //
    // O `valueChanges` da tela dispara para qualquer campo, inclusive os que
    // não alimentam este cartão. Publicar sempre faria o cartão reconsultar o
    // banco porque alguém digitou numa observação — e numa tela com quatro
    // indicadores seriam quatro consultas por tecla.
    if (!changed && this.filterBus.value !== null) {
      return;
    }

    this.filterBus.next(this.synthetic);
  }

  private valueOf(
    binding: DashboardFilterBinding,
    screenForm: FormGroup | null,
    screenData: unknown
  ): unknown {

    if (binding.sourceNodeId !== undefined && screenForm !== null) {
      const control = screenForm.get(binding.sourceNodeId);

      if (control !== null) {
        return control.value;
      }
    }

    if (binding.sourceDataPath !== undefined) {
      return resolvePath(screenData, binding.sourceDataPath);
    }

    return null;
  }
}
